import { useEffect } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { ROUTES } from "./paths";

/** Prefix every tab shows, so the app is recognisable among a row of tabs. */
const BRAND = "Naurist";

/**
 * Title per route, in the order the router itself resolves them: `addClient`
 * has to be tested before `myClientDetail`, or "/my-clients/new" would match
 * the `:clientId` pattern and read "Client details".
 */
const PAGE_TITLES: ReadonlyArray<readonly [path: string, title: string]> = [
  [ROUTES.signup, "Sign up"],
  [ROUTES.consent, "Before we continue"],
  [ROUTES.verifyOtp, "Verify OTP"],
  [ROUTES.login, "Login"],
  [ROUTES.ssoCallback, "Signing in"],

  // The same heading as the sign-up consent screen, because it is the same
  // agreement being asked for.
  [ROUTES.consentRequired, "Before we continue"],
  [ROUTES.dashboard, "Dashboard"],
  [ROUTES.addClient, "Add client"],
  // Ahead of the detail route it sits under, so "/my-clients/<id>/edit" does
  // not read "Client details".
  [ROUTES.editClient, "Edit client"],
  [ROUTES.myClientDetail, "Client details"],
  [ROUTES.myClients, "My clients"],
  // Ahead of `enquiries`, for the same reason `addClient` comes before the
  // client detail route.
  [ROUTES.addEnquiry, "Add enquiry"],
  // Ahead of the detail route it sits under, so "/enquiries/<id>/edit" does
  // not read "Enquiry details".
  [ROUTES.editEnquiry, "Edit enquiry"],
  [ROUTES.enquiryDetail, "Enquiry details"],
  [ROUTES.enquiries, "Enquiries"],
  // Ahead of `createPlan`, for the same reason `addClient` comes before the
  // client detail route: the more specific path has to be tested first.
  [ROUTES.planDetail, "Meal plan"],
  [ROUTES.createPlan, "Create plan"],
  [ROUTES.createSupplement, "Create supplement"],
  [ROUTES.dishes, "Dishes"],
  [ROUTES.revenue, "Revenue"],
  [ROUTES.myBrand, "Brand setup"],
  // "Create page" in the rail, "My profile page" once you are on it — the item
  // names the thing you go and do, the screen names the thing itself. The tab
  // follows the screen, as every other entry here does.
  [ROUTES.createPage, "My profile page"],
  [ROUTES.launchApp, "Launch app"],
  // The result path is longer than the checkout it sits under, so it is tested
  // first — the same ordering `addClient` gets above the client detail route.
  // `/billing` itself has no entry: it only redirects to the Settings screen's
  // Billings tab now, and the title that sticks is that screen's.
  [ROUTES.billingResult, "Subscription"],
  [ROUTES.billingCheckout, "Checkout"],
  // Longest first, so `/create-account/monthly/started` does not read "Your
  // details" and `/create-account/monthly` does not read "Create account" —
  // the same ordering the two billing paths above take.
  [ROUTES.trialStarted, "Trial started"],
  [ROUTES.trialDetails, "Your details"],
  // "Create account", not "Free trial": the tab says which part of signing up
  // somebody is on, the way the screen's own URL does.
  [ROUTES.trial, "Create account"],
  // Ahead of `settings`, which it sits under — same ordering as `addClient`
  // above the client detail route.
  [ROUTES.bookCall, "Book a call"],
  [ROUTES.settings, "Settings"],
];

/** The tab title for a path, e.g. "Naurist | My brand". */
export const titleForPath = (pathname: string): string => {
  const match = PAGE_TITLES.find(([path]) =>
    matchPath({ path, end: true }, pathname)
  );
  return match ? `${BRAND} | ${match[1]}` : BRAND;
};

/**
 * Keeps `document.title` in step with the URL. Rendered once inside the
 * router rather than called from each screen, so a new route only needs an
 * entry in the table above.
 */
const DocumentTitle: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    document.title = titleForPath(pathname);
  }, [pathname]);

  return null;
};

export default DocumentTitle;
